import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Loader2,
  ShieldCheck,
  Sliders,
  Users,
  Calendar,
  History,
  Info,
  Eye,
  EyeOff,
  Trash2,
  Edit,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { api } from '@/lib/api';

interface UserItem {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

interface TenantProfile {
  id: string;
  name: string;
  slug: string;
  email: string;
  phone: string | null;
  subscriptionTier: string;
  isActive: boolean;
  maxUsersAllowed: number;
  subscriptionEnd: string | null;
  ownerName: string;
  featureFlags: {
    bulkMatrix: boolean;
    gstComplianceExport: boolean;
    loyaltyEngine: boolean;
  };
  overrideLogs: Array<{
    date: string;
    daysAdded: number;
    paymentRef: string;
  }>;
}

interface TenantDetailDrillDownProps {
  tenantId: string;
  onBack: () => void;
}

export function TenantDetailDrillDown({ tenantId, onBack }: TenantDetailDrillDownProps) {
  const [profile, setProfile] = useState<TenantProfile | null>(null);
  const [users, setUsers] = useState<UserItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Form Fields
  const [maxUsers, setMaxUsers] = useState<number>(5);
  const [expiryDate, setExpiryDate] = useState<string>('');
  const [planTier, setPlanTier] = useState<'Silver' | 'Gold' | 'Platinum' | 'Custom'>('Silver');
  const [plans, setPlans] = useState<any[]>([]);
  const [subscriptionPlanId, setSubscriptionPlanId] = useState<string>('');
  const [ownerName, setOwnerName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [isWorkspaceActive, setIsWorkspaceActive] = useState<boolean>(true);

  // Feature Flags
  const [bulkMatrix, setBulkMatrix] = useState<boolean>(false);
  const [gstComplianceExport, setGstComplianceExport] = useState<boolean>(false);
  const [loyaltyEngine, setLoyaltyEngine] = useState<boolean>(false);

  // Override State
  const [overridePaymentRef, setOverridePaymentRef] = useState<string>('');
  const [overrideDays, setOverrideDays] = useState<number>(0);

  // Tenant Delete confirmation states
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteConfirmPassword, setDeleteConfirmPassword] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showVerifyDeletePassword, setShowVerifyDeletePassword] = useState<boolean>(false);

  // User Management & Provisioning Form States
  const [newUserForm, setNewUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'SALESPERSON' as 'TENANT_ADMIN' | 'MANAGER' | 'SALESPERSON' | 'VIEWER',
  });
  const [newUserSaving, setNewUserSaving] = useState(false);
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  // User Inline Editing States
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editUserForm, setEditUserForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'SALESPERSON' as 'TENANT_ADMIN' | 'MANAGER' | 'SALESPERSON' | 'VIEWER',
    is_active: true,
  });
  const [editUserSaving, setEditUserSaving] = useState(false);

  // Password Reset States
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordResetSaving, setPasswordResetSaving] = useState(false);

  const fetchLicensingProfile = async () => {
    setIsLoading(true);
    setError('');
    try {
      const plansRes = await api.get('/super/plans');
      if (plansRes.data && plansRes.data.success) {
        setPlans(plansRes.data.data);
      }

      const response = await api.get(`/super/tenants/${tenantId}/licensing-profile`);
      const { tenant, users } = response.data.data;
      setProfile(tenant);
      setUsers(users);

      // Populate Form Fields
      setMaxUsers(tenant.maxUsersAllowed);
      setOwnerName(tenant.ownerName || '');
      setEmail(tenant.email || '');
      setPhone(tenant.phone || '');
      setIsWorkspaceActive(tenant.isActive);
      setSubscriptionPlanId(tenant.subscriptionPlanId || '');

      // Feature Flags
      const bMatrix = tenant.featureFlags?.bulkMatrix ?? false;
      const gstExport = tenant.featureFlags?.gstComplianceExport ?? false;
      const lEngine = tenant.featureFlags?.loyaltyEngine ?? false;

      setBulkMatrix(bMatrix);
      setGstComplianceExport(gstExport);
      setLoyaltyEngine(lEngine);

      // Sync local storage flags for real-time sandbox gating
      localStorage.setItem(
        `tenant_flags_${tenantId}`,
        JSON.stringify({
          bulkMatrix: bMatrix,
          gstComplianceExport: gstExport,
          loyaltyEngine: lEngine,
        }),
      );

      const mapping: Record<string, 'Silver' | 'Gold' | 'Platinum'> = {
        STARTER: 'Silver',
        PROFESSIONAL: 'Gold',
        ENTERPRISE: 'Platinum',
        FREE: 'Silver',
      };
      setPlanTier(mapping[tenant.subscriptionTier] || 'Silver');

      if (tenant.subscriptionEnd) {
        setExpiryDate(new Date(tenant.subscriptionEnd).toISOString().split('T')[0] ?? '');
      } else {
        setExpiryDate('');
      }

      // Reset overrides
      setOverrideDays(0);
      setOverridePaymentRef('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load tenant profile.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLicensingProfile();
  }, [tenantId]);

  const handleApplyOverrideDays = (days: number) => {
    setOverrideDays(days);
    if (!overridePaymentRef) {
      setOverridePaymentRef(`OVERRIDE_${days}D_REF_${Math.floor(100000 + Math.random() * 900000)}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      // Map expiry date to UTC midnight ISO string
      let finalExpiry = expiryDate ? new Date(expiryDate).toISOString() : new Date().toISOString();

      const payload: Record<string, any> = {
        max_users_allowed: maxUsers,
        subscription_end: finalExpiry,
        plan_tier: subscriptionPlanId ? undefined : planTier === 'Custom' ? undefined : planTier,
        subscription_plan_id: subscriptionPlanId || null,
        owner_name: ownerName,
        email: email,
        phone: phone,
        is_active: isWorkspaceActive,
        feature_flags: {
          bulkMatrix,
          gstComplianceExport,
          loyaltyEngine,
        },
      };

      if (overrideDays > 0) {
        payload.override_days_to_add = overrideDays;
        payload.override_payment_ref = overridePaymentRef;
      }

      await api.put(`/super/tenants/${tenantId}/licensing-profile`, payload);

      // Sync local storage flags for real-time sandbox gating
      localStorage.setItem(
        `tenant_flags_${tenantId}`,
        JSON.stringify({
          bulkMatrix,
          gstComplianceExport,
          loyaltyEngine,
        }),
      );

      setSuccessMsg('Licensing constraints and settings updated successfully.');
      setTimeout(() => setSuccessMsg(''), 4000);

      // Reload profile
      await fetchLicensingProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!newPassword.trim() || newPassword.length < 4) {
      setError('Password must be at least 4 characters long.');
      return;
    }

    setPasswordResetSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      await api.put(`/super/tenants/${tenantId}/users/${userId}/reset-password`, {
        password: newPassword,
      });

      setSuccessMsg('User password reset successfully.');
      setResettingUserId(null);
      setNewPassword('');
      setTimeout(() => setSuccessMsg(''), 4000);

      // Reload profile to reflect changes
      await fetchLicensingProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setPasswordResetSaving(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    setError('');
    setSuccessMsg('');
    try {
      await api.delete(`/super/tenants/${tenantId}/users/${userId}`);
      setSuccessMsg('User account deleted successfully.');
      setTimeout(() => setSuccessMsg(''), 4000);
      await fetchLicensingProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user.');
    }
  };

  const handleDeleteTenant = async () => {
    if (!deleteConfirmPassword.trim()) {
      setError('Super admin password is required to delete the tenant workspace.');
      return;
    }

    setIsDeleting(true);
    setError('');
    setSuccessMsg('');

    try {
      await api.delete(`/super/tenants/${tenantId}`, {
        data: { password: deleteConfirmPassword },
      });
      setSuccessMsg('Tenant workspace permanently deleted.');
      setShowDeleteModal(false);
      setDeleteConfirmPassword('');
      setTimeout(() => {
        setSuccessMsg('');
        onBack();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete tenant workspace.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditUserSubmit = async (userId: string) => {
    if (!editUserForm.first_name.trim() || !editUserForm.last_name.trim() || !editUserForm.email.trim()) {
      setError('Please fill in all user details.');
      return;
    }
    setEditUserSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      await api.put(`/super/tenants/${tenantId}/users/${userId}`, {
        first_name: editUserForm.first_name,
        last_name: editUserForm.last_name,
        email: editUserForm.email,
        role: editUserForm.role,
        is_active: editUserForm.is_active,
      });
      setSuccessMsg('User account updated successfully.');
      setEditingUserId(null);
      setTimeout(() => setSuccessMsg(''), 4000);
      await fetchLicensingProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user.');
    } finally {
      setEditUserSaving(false);
    }
  };

  const handleCreateUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewUserSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      await api.post(`/super/tenants/${tenantId}/users`, newUserForm);
      setSuccessMsg('New staff login credentials provisioned successfully.');
      setNewUserForm({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role: 'SALESPERSON',
      });
      setTimeout(() => setSuccessMsg(''), 4000);

      // Refresh listings
      await fetchLicensingProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to provision staff login.');
    } finally {
      setNewUserSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="card flex flex-col items-center justify-center py-24 text-slate-400 bg-white border border-slate-100 rounded-xl">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mb-2" />
        <span className="text-xs font-semibold">Retrieving secure workspace profile...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-slate-800 font-sans antialiased">
      {/* Detail Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-slate-150 pb-4 gap-3">
        <div className="flex items-center space-x-3">
          <button
            onClick={onBack}
            className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-all cursor-pointer shadow-sm"
          >
            <ArrowLeft size={16} className="text-slate-600" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-slate-900">{profile?.name}</h2>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[11px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                {profile?.slug}
              </span>
              <span className="text-slate-300 text-xs">•</span>
              <span className="text-xs text-slate-500 font-medium">
                Owner: {profile?.ownerName || 'N/A'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${
              profile?.isActive
                ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                : 'bg-rose-50 text-rose-700 border-rose-100'
            }`}
          >
            {profile?.isActive ? 'ACTIVE WORKSPACE' : 'SUSPENDED'}
          </span>
        </div>
      </div>

      {successMsg && (
        <div className="p-4 bg-emerald-50 border border-emerald-250 text-xs text-emerald-800 font-semibold rounded-xl flex items-center gap-2.5">
          <CheckCircle size={16} className="text-emerald-600" />
          <span>{successMsg}</span>
        </div>
      )}

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-xs text-rose-700 font-semibold rounded-xl flex items-center gap-2.5">
          <AlertCircle size={16} className="text-rose-600" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Core Settings Form (Left 1/3) */}
        <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-5 h-fit">
          <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-3.5 flex items-center gap-2">
            <Sliders className="text-emerald-600" size={16} />
            <span>Licensing Controls</span>
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4 text-xs">
            {/* General Profile fields */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Owner Full Name
              </label>
              <input
                type="text"
                required
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-medium"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-medium"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Contact Phone
                </label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-mono"
                />
              </div>
            </div>

            <div className="border-t border-slate-100 pt-3.5 space-y-4">
              {/* Max Users Allowed */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Max Permitted Users (User Cap Limit)
                </label>
                <input
                  type="number"
                  min="1"
                  required
                  value={maxUsers}
                  onChange={(e) => setMaxUsers(parseInt(e.target.value) || 1)}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-mono font-bold"
                />
              </div>

              {/* Expiration Tracker */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Subscription End Date
                </label>
                <input
                  type="date"
                  required
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-mono cursor-pointer font-bold"
                />
              </div>

              {/* Plan Tier */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Plan Tier Level
                </label>
                <select
                  value={subscriptionPlanId ? `custom-${subscriptionPlanId}` : planTier}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.startsWith('custom-')) {
                      const planId = val.replace('custom-', '');
                      setSubscriptionPlanId(planId);
                      setPlanTier('Custom' as any);
                      const selectedPlan = plans.find((p) => p.id === planId);
                      if (selectedPlan) {
                        setMaxUsers(selectedPlan.maxUsers);
                        const planFeatures =
                          typeof selectedPlan.features === 'string'
                            ? JSON.parse(selectedPlan.features)
                            : (selectedPlan.features as Record<string, any>) || {};
                        setBulkMatrix(!!planFeatures.bulkMatrix);
                        setGstComplianceExport(!!planFeatures.gstComplianceExport);
                        setLoyaltyEngine(!!planFeatures.loyaltyEngine);
                      }
                    } else {
                      setSubscriptionPlanId('');
                      setPlanTier(val as any);
                      if (val === 'Silver') {
                        setMaxUsers(5);
                        setBulkMatrix(false);
                        setGstComplianceExport(false);
                        setLoyaltyEngine(false);
                      } else if (val === 'Gold') {
                        setMaxUsers(15);
                        setBulkMatrix(true);
                        setGstComplianceExport(false);
                        setLoyaltyEngine(false);
                      } else if (val === 'Platinum') {
                        setMaxUsers(999);
                        setBulkMatrix(true);
                        setGstComplianceExport(true);
                        setLoyaltyEngine(true);
                      }
                    }
                  }}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 cursor-pointer font-bold bg-white"
                >
                  <optgroup label="Standard Presets">
                    <option value="Silver">Silver (Starter)</option>
                    <option value="Gold">Gold (Professional)</option>
                    <option value="Platinum">Platinum (Enterprise)</option>
                  </optgroup>
                  {plans.length > 0 && (
                    <optgroup label="Dynamic Database Plans">
                      {plans.map((p: any) => (
                        <option key={p.id} value={`custom-${p.id}`}>
                          {p.name} (₹{p.price}/mo)
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              {/* Workspace Status Toggle */}
              <div className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200/60 rounded-lg">
                <div>
                  <span className="font-bold text-slate-700 block">Workspace Active</span>
                  <span className="text-[9px] text-slate-400">
                    Suspended tenants cannot use billing.
                  </span>
                </div>
                <input
                  type="checkbox"
                  checked={isWorkspaceActive}
                  onChange={(e) => setIsWorkspaceActive(e.target.checked)}
                  className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer"
                />
              </div>
            </div>

            {/* Feature Flags mapping switches */}
            <div className="border-t border-slate-100 pt-3.5 space-y-2.5">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                Gated Feature Switches
              </label>

              <div className="flex items-center justify-between text-[11px] font-medium text-slate-650">
                <span>Bulk Matrix Intake Matrix</span>
                <input
                  type="checkbox"
                  checked={bulkMatrix}
                  onChange={(e) => setBulkMatrix(e.target.checked)}
                  className="w-3.5 h-3.5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-between text-[11px] font-medium text-slate-650">
                <span>GST Governance Engine</span>
                <input
                  type="checkbox"
                  checked={gstComplianceExport}
                  onChange={(e) => setGstComplianceExport(e.target.checked)}
                  className="w-3.5 h-3.5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-between text-[11px] font-medium text-slate-650">
                <span>Customer Loyalty Engine</span>
                <input
                  type="checkbox"
                  checked={loyaltyEngine}
                  onChange={(e) => setLoyaltyEngine(e.target.checked)}
                  className="w-3.5 h-3.5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSaving}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5 cursor-pointer shadow-md hover:shadow transition-all"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Syncing Parameters...</span>
                </>
              ) : (
                <>
                  <ShieldCheck size={15} />
                  <span>Update Tenant Config</span>
                </>
              )}
            </button>
          </form>

          {/* Danger Zone */}
          <div className="border-t border-slate-100 pt-4 mt-6 space-y-3">
            <h4 className="text-xs font-bold text-rose-700 uppercase tracking-wider flex items-center gap-1.5">
              <Trash2 className="text-rose-600 animate-pulse" size={15} />
              <span>Danger Zone</span>
            </h4>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Permanently delete this entire tenant workspace. All products, categories, staff accounts, invoices, and returns will be cascadingly purged from the database forever.
            </p>
            <button
              type="button"
              onClick={() => {
                setDeleteConfirmPassword('');
                setShowDeleteModal(true);
              }}
              className="w-full py-2 bg-rose-50 hover:bg-rose-100 border border-rose-200 hover:border-rose-300 text-rose-700 font-bold rounded-lg cursor-pointer transition-all flex items-center justify-center gap-1.5 text-xs shadow-sm"
            >
              <Trash2 size={13} />
              <span>Delete Tenant Workspace</span>
            </button>
          </div>
        </div>

        {/* Dynamic Right Side Workspace Profile (Right 2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Offline Manual Override panel */}
          <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
              <Calendar className="text-emerald-600" size={16} />
              <span>Offline Manual Override Matrix</span>
            </h3>

            <div className="text-xs space-y-4">
              <div className="p-3 bg-amber-50/50 border border-amber-200/50 rounded-lg text-[11px] text-amber-800 flex gap-2">
                <Info size={16} className="shrink-0 mt-0.5 text-amber-600" />
                <p className="leading-relaxed">
                  Use this override matrix to manually extend the subscription end date instantly.
                  You can enter an offline/bank payment reference transaction ledger identifier to
                  lock in the change record.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Override Ledger Payment Reference
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. IMPS/HDFC/492940294"
                    value={overridePaymentRef}
                    onChange={(e) => setOverridePaymentRef(e.target.value)}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-mono placeholder:text-slate-350"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                    Add Days Override
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleApplyOverrideDays(30)}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all cursor-pointer ${
                        overrideDays === 30
                          ? 'bg-emerald-600 text-white border-emerald-600 shadow'
                          : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      +30 Days
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApplyOverrideDays(60)}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all cursor-pointer ${
                        overrideDays === 60
                          ? 'bg-emerald-600 text-white border-emerald-600 shadow'
                          : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      +60 Days
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApplyOverrideDays(90)}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all cursor-pointer ${
                        overrideDays === 90
                          ? 'bg-emerald-600 text-white border-emerald-600 shadow'
                          : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      +90 Days
                    </button>
                  </div>
                </div>
              </div>

              {overrideDays > 0 && (
                <div className="p-3.5 bg-emerald-50 rounded-lg border border-emerald-100 flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-emerald-800 text-[11px] block">
                      Pending Override Application:
                    </span>
                    <span className="text-[10px] text-slate-500">
                      Adding <span className="font-bold text-emerald-700">{overrideDays} Days</span>{' '}
                      under ledger ref{' '}
                      <span className="font-mono font-bold text-slate-700">
                        {overridePaymentRef}
                      </span>
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setOverrideDays(0);
                      setOverridePaymentRef('');
                    }}
                    className="text-[10px] text-slate-400 hover:text-slate-600 font-bold uppercase underline cursor-pointer"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* User Registry Monitor */}
          <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Users className="text-emerald-600" size={16} />
                <span>Client Staff Compliance Grid</span>
              </div>
              <span className="bg-slate-100 text-slate-650 px-2 py-0.5 rounded-full text-[10px] font-bold">
                {users.length} Registered Cashiers
              </span>
            </h3>

            {users.length === 0 ? (
              <div className="text-center py-12 text-slate-450">
                <Users size={36} className="mx-auto mb-2.5 opacity-20 text-emerald-600" />
                <p className="text-xs font-bold">
                  No cashier accounts or team members onboarded in this workspace.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto border border-slate-100 rounded-lg">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider bg-slate-50/50">
                      <th className="py-2.5 px-3 text-center w-12">S.No.</th>
                      <th className="py-2.5 px-3">Team Member Name & ID</th>
                      <th className="py-2.5 px-2">Role Label</th>
                      <th className="py-2.5 px-2 text-center">Registration Date</th>
                      <th className="py-2.5 px-3 text-center">Telemetry Status</th>
                      <th className="py-2.5 px-3 text-right">Access Controls</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {users.map((user, idx) => (
                      editingUserId === user.id ? (
                        <tr key={user.id} className="bg-slate-50/70 hover:bg-slate-50">
                          <td className="py-3 px-3 text-center text-slate-500 font-medium">{idx + 1}</td>
                          <td className="py-3 px-3 space-y-1.5">
                            <div className="flex gap-1.5">
                              <input
                                type="text"
                                value={editUserForm.first_name}
                                onChange={(e) => setEditUserForm({ ...editUserForm, first_name: e.target.value })}
                                className="px-2 py-1 text-[11px] border border-slate-200 rounded focus:outline-none focus:border-emerald-600 font-medium bg-white w-20"
                                placeholder="First"
                              />
                              <input
                                type="text"
                                value={editUserForm.last_name}
                                onChange={(e) => setEditUserForm({ ...editUserForm, last_name: e.target.value })}
                                className="px-2 py-1 text-[11px] border border-slate-200 rounded focus:outline-none focus:border-emerald-600 font-medium bg-white w-20"
                                placeholder="Last"
                              />
                            </div>
                            <input
                              type="email"
                              value={editUserForm.email}
                              onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                              className="px-2 py-1 text-[10px] border border-slate-200 rounded focus:outline-none focus:border-emerald-600 font-mono bg-white w-full"
                              placeholder="Email/User ID"
                            />
                          </td>
                          <td className="py-3 px-2">
                            <select
                              value={editUserForm.role}
                              onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value as any })}
                              className="px-2 py-1 text-[10px] border border-slate-200 rounded bg-white cursor-pointer"
                            >
                              <option value="TENANT_ADMIN">TENANT_ADMIN</option>
                              <option value="MANAGER">MANAGER</option>
                              <option value="SALESPERSON">SALESPERSON</option>
                              <option value="VIEWER">VIEWER</option>
                            </select>
                          </td>
                          <td className="py-3 px-2 text-center text-slate-400 font-mono">
                            -
                          </td>
                          <td className="py-3 px-3 text-center">
                            <select
                              value={editUserForm.is_active ? "active" : "inactive"}
                              onChange={(e) => setEditUserForm({ ...editUserForm, is_active: e.target.value === "active" })}
                              className="px-2 py-1 text-[10px] border border-slate-200 rounded bg-white cursor-pointer"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Suspended</option>
                            </select>
                          </td>
                          <td className="py-3 px-3 text-right space-x-1.5">
                            <button
                              type="button"
                              onClick={() => handleEditUserSubmit(user.id)}
                              disabled={editUserSaving}
                              className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold cursor-pointer inline-flex items-center gap-1 shadow-sm"
                            >
                              {editUserSaving ? <Loader2 size={10} className="animate-spin" /> : null}
                              <span>Save</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingUserId(null)}
                              className="px-2 py-1 bg-slate-200 hover:bg-slate-250 text-slate-700 rounded text-[10px] font-bold cursor-pointer"
                            >
                              Cancel
                            </button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={user.id} className="hover:bg-slate-50/50">
                          <td className="py-3 px-3 text-center text-slate-500 font-medium">{idx + 1}</td>
                          <td className="py-3 px-3">
                            <span className="font-bold text-slate-850 block">{user.name}</span>
                            <span className="text-[10px] text-slate-400 font-mono mt-0.5 block">
                              {user.email}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <span className="px-2 py-0.5 bg-slate-100 border border-slate-150 text-slate-600 rounded text-[9px] font-bold uppercase">
                              {user.role}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center text-slate-500 font-mono">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span
                              className={`inline-flex items-center gap-1 text-[9px] px-2 py-0.5 font-bold rounded-full ${
                                user.isActive
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                                  : 'bg-rose-50 text-rose-700 border-rose-100'
                              }`}
                            >
                              {user.isActive ? (
                                <>
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                  <span>Active</span>
                                </>
                              ) : (
                                <>
                                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                                  <span>Suspended</span>
                                </>
                              )}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-right font-medium">
                            {resettingUserId === user.id ? (
                              <div className="inline-flex items-center gap-1 justify-end animate-fadeIn">
                                <div className="relative flex items-center">
                                  <input
                                    type={showResetPassword ? "text" : "password"}
                                    placeholder="New password..."
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="pl-2 pr-7 py-1 text-[10px] border border-slate-200 rounded focus:outline-none focus:border-emerald-600 font-mono w-28 bg-white"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowResetPassword(!showResetPassword)}
                                    className="absolute right-1.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                                    title={showResetPassword ? "Hide password" : "Show password"}
                                  >
                                    {showResetPassword ? <EyeOff size={11} /> : <Eye size={11} />}
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleResetPassword(user.id)}
                                  disabled={passwordResetSaving}
                                  className="px-1.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[9px] font-bold cursor-pointer"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setResettingUserId(null);
                                    setNewPassword('');
                                  }}
                                  className="px-1.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-[9px] font-bold cursor-pointer"
                                >
                                  X
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-end gap-3.5">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingUserId(user.id);
                                    setEditUserForm({
                                      email: user.email,
                                      first_name: user.firstName,
                                      last_name: user.lastName,
                                      role: user.role as any,
                                      is_active: user.isActive,
                                    });
                                  }}
                                  className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 hover:text-blue-800 font-bold hover:underline cursor-pointer"
                                  title="Edit details"
                                >
                                  <Edit size={11} />
                                  <span>Edit</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteUser(user.id)}
                                  className="inline-flex items-center gap-0.5 text-[10px] text-rose-600 hover:text-rose-800 font-bold hover:underline cursor-pointer"
                                  title="Delete Account"
                                >
                                  <Trash2 size={11} />
                                  <span>Delete</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setResettingUserId(user.id);
                                    setNewPassword('');
                                    setShowResetPassword(false);
                                  }}
                                  className="text-[10px] text-emerald-600 hover:text-emerald-700 font-bold underline cursor-pointer"
                                >
                                  Reset PW
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Direct User Provisioning Form */}
            <div className="border-t border-slate-100 pt-4 mt-4 text-xs">
              <h4 className="font-bold text-slate-800 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <span>Provision New Login Credentials</span>
              </h4>
              <form
                onSubmit={handleCreateUserSubmit}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end"
              >
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                    First Name
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Anil"
                    value={newUserForm.first_name}
                    onChange={(e) => setNewUserForm({ ...newUserForm, first_name: e.target.value })}
                    className="block w-full px-2.5 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-medium"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                    Last Name
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Kumar"
                    value={newUserForm.last_name}
                    onChange={(e) => setNewUserForm({ ...newUserForm, last_name: e.target.value })}
                    className="block w-full px-2.5 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-medium"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                    Email Address
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="e.g. anil@store.com"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                    className="block w-full px-2.5 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-medium"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                    Role
                  </label>
                  <select
                    value={newUserForm.role}
                    onChange={(e) =>
                      setNewUserForm({ ...newUserForm, role: e.target.value as any })
                    }
                    className="block w-full px-2.5 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-bold bg-white cursor-pointer"
                  >
                    <option value="TENANT_ADMIN">Tenant Admin</option>
                    <option value="MANAGER">Store Manager</option>
                    <option value="SALESPERSON">Cashier / Salesperson</option>
                    <option value="VIEWER">Auditor / Viewer</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                    Login Password
                  </label>
                  <div className="relative flex items-center">
                    <input
                      type={showNewUserPassword ? "text" : "password"}
                      required
                      placeholder="e.g. welcome123"
                      value={newUserForm.password}
                      onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                      className="block w-full pl-2.5 pr-8 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600 font-mono text-xs"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewUserPassword(!showNewUserPassword)}
                      className="absolute right-2 text-slate-400 hover:text-slate-600 cursor-pointer"
                      title={showNewUserPassword ? "Hide password" : "Show password"}
                    >
                      {showNewUserPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div className="sm:col-span-2 lg:col-span-5 flex justify-end">
                  <button
                    type="submit"
                    disabled={newUserSaving}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5 cursor-pointer shadow-sm"
                  >
                    {newUserSaving ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Provisioning Account...</span>
                      </>
                    ) : (
                      <>
                        <ShieldCheck size={14} />
                        <span>Provision Credentials</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Override Logs Payment Ledger */}
          <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
              <History className="text-emerald-600" size={16} />
              <span>Offline Override Payment Ledger Logs</span>
            </h3>

            {!profile?.overrideLogs || profile.overrideLogs.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <p className="text-xs font-medium">
                  No override ledger records exist for this tenant.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto border border-slate-100 rounded-lg">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider bg-slate-50/50">
                      <th className="py-2.5 px-3 text-center w-12">S.No.</th>
                      <th className="py-2.5 px-3">Date Applied</th>
                      <th className="py-2.5 px-2">Days Added</th>
                      <th className="py-2.5 px-3">Payment Reference Ledger Identifier</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {profile.overrideLogs.map((log, index) => (
                      <tr key={index} className="hover:bg-slate-50/50">
                        <td className="py-2.5 px-3 text-center text-slate-500 font-medium">{index + 1}</td>
                        <td className="py-2.5 px-3 text-slate-600 font-mono">
                          {new Date(log.date).toLocaleString()}
                        </td>
                        <td className="py-2.5 px-2">
                          <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 font-bold rounded">
                            +{log.daysAdded} Days
                          </span>
                        </td>
                        <td className="py-2.5 px-3 font-mono font-bold text-slate-700">
                          {log.paymentRef}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-100 animate-scaleUp">
            <div className="flex justify-between items-center p-5 border-b border-slate-100 bg-rose-50/20">
              <span className="text-xs font-bold text-rose-800 uppercase tracking-wider flex items-center gap-1.5">
                <AlertCircle className="text-rose-600 animate-bounce" size={16} />
                <span>Confirm Permanent Deletion</span>
              </span>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmPassword('');
                }}
                className="text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="space-y-1.5">
                <h4 className="text-sm font-bold text-slate-800">
                  Are you absolutely sure you want to delete {profile?.name}?
                </h4>
                <p className="text-xs text-slate-500 leading-relaxed">
                  This action is irreversible. It will wipe all users, products, sales invoices, stock history, and settings from the database.
                </p>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                  Verify Super Admin Password
                </label>
                <div className="relative flex items-center">
                  <input
                    type={showVerifyDeletePassword ? "text" : "password"}
                    required
                    placeholder="Enter your superadmin password"
                    value={deleteConfirmPassword}
                    onChange={(e) => setDeleteConfirmPassword(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-rose-500 font-mono text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => setShowVerifyDeletePassword(!showVerifyDeletePassword)}
                    className="absolute right-3 text-slate-400 hover:text-slate-650 cursor-pointer"
                    title={showVerifyDeletePassword ? "Hide password" : "Show password"}
                  >
                    {showVerifyDeletePassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmPassword('');
                }}
                className="flex-1 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-lg shadow-sm cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTenant}
                disabled={isDeleting || !deleteConfirmPassword.trim()}
                className="flex-1 py-2 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-xs font-bold rounded-lg shadow border border-rose-500 active:scale-[0.98] transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Deleting Workspace...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={13} />
                    <span>Permanently Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
